import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Shield, Users, Lock, Eye, Settings, Plus, Trash2, Edit, 
  CheckCircle2, XCircle, AlertTriangle, RefreshCw, Activity,
  Database, Building, FileText, UserCheck, Key, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import OperationsTasksManager from '../components/OperationsTasksManager';

const API = process.env.REACT_APP_BACKEND_URL;

const ENTITY_TYPES = ['contacts', 'companies', 'deals', 'tickets', 'campaigns', 'reports', 'settings', 'users', 'operations'];
const PERMISSIONS = ['read', 'write', 'delete', 'export'];

const OperationsHubPage = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  
  // Dashboard
  const [dashboard, setDashboard] = useState(null);
  const [entityStats, setEntityStats] = useState(null);
  
  // Partitions
  const [partitions, setPartitions] = useState([]);
  const [partitionModalOpen, setPartitionModalOpen] = useState(false);
  const [partitionForm, setPartitionForm] = useState({
    name: '',
    description: '',
    partition_type: 'team',
    entity_types: [],
    is_active: true
  });
  
  // Roles
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [roleForm, setRoleForm] = useState({
    role_name: '',
    description: '',
    permissions: {}
  });
  
  // Isolation Policies
  const [policies, setPolicies] = useState([]);
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [policyForm, setPolicyForm] = useState({
    name: '',
    description: '',
    isolation_type: 'row_level',
    entity_type: 'contacts',
    isolation_field: 'owner_id',
    enforcement_level: 'strict',
    is_active: true
  });
  
  // Access Logs
  const [accessLogs, setAccessLogs] = useState([]);

  const getToken = () => localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${getToken()}` };

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/operations/dashboard`, { headers });
      setDashboard(res.data);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
    }
  }, []);

  const fetchEntityStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/operations/stats/entities`, { headers });
      setEntityStats(res.data);
    } catch (err) {
      console.error('Failed to fetch entity stats:', err);
    }
  }, []);

  const fetchPartitions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/operations/partitions`, { headers });
      setPartitions(res.data);
    } catch (err) {
      console.error('Failed to fetch partitions:', err);
    }
  }, []);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/operations/roles`, { headers });
      setRoles(res.data);
    } catch (err) {
      console.error('Failed to fetch roles:', err);
    }
  }, []);

  const fetchPolicies = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/operations/isolation-policies`, { headers });
      setPolicies(res.data);
    } catch (err) {
      console.error('Failed to fetch policies:', err);
    }
  }, []);

  const fetchAccessLogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/operations/access-logs?limit=50`, { headers });
      setAccessLogs(res.data);
    } catch (err) {
      console.error('Failed to fetch access logs:', err);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchEntityStats();
    fetchPartitions();
    fetchRoles();
    fetchPolicies();
    fetchAccessLogs();
  }, []);

  // Partition CRUD
  const createPartition = async () => {
    if (!partitionForm.name) {
      toast.error('Name is required');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/operations/partitions`, partitionForm, { headers });
      fetchPartitions();
      setPartitionModalOpen(false);
      setPartitionForm({ name: '', description: '', partition_type: 'team', entity_types: [], is_active: true });
      toast.success('Partition created');
    } catch (err) {
      toast.error('Failed to create partition');
    } finally {
      setLoading(false);
    }
  };

  const deletePartition = async (id) => {
    if (!confirm('Delete this partition?')) return;
    try {
      await axios.delete(`${API}/api/operations/partitions/${id}`, { headers });
      fetchPartitions();
      toast.success('Partition deleted');
    } catch (err) {
      toast.error('Failed to delete partition');
    }
  };

  // Role CRUD
  const createRole = async () => {
    if (!roleForm.role_name) {
      toast.error('Role name is required');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/operations/roles`, roleForm, { headers });
      fetchRoles();
      setRoleModalOpen(false);
      setRoleForm({ role_name: '', description: '', permissions: {} });
      toast.success('Role created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create role');
    } finally {
      setLoading(false);
    }
  };

  const updateRolePermission = async (roleId, entity, permission, value) => {
    const role = roles.find(r => r.id === roleId);
    if (!role) return;
    
    const newPermissions = { ...role.permissions };
    if (!newPermissions[entity]) {
      newPermissions[entity] = { read: false, write: false, delete: false, export: false };
    }
    newPermissions[entity][permission] = value;
    
    try {
      await axios.put(`${API}/api/operations/roles/${roleId}`, { permissions: newPermissions }, { headers });
      fetchRoles();
    } catch (err) {
      toast.error('Failed to update permission');
    }
  };

  // Policy CRUD
  const createPolicy = async () => {
    if (!policyForm.name) {
      toast.error('Name is required');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/operations/isolation-policies`, policyForm, { headers });
      fetchPolicies();
      setPolicyModalOpen(false);
      setPolicyForm({ name: '', description: '', isolation_type: 'row_level', entity_type: 'contacts', isolation_field: 'owner_id', enforcement_level: 'strict', is_active: true });
      toast.success('Policy created');
    } catch (err) {
      toast.error('Failed to create policy');
    } finally {
      setLoading(false);
    }
  };

  const deletePolicy = async (id) => {
    if (!confirm('Delete this policy?')) return;
    try {
      await axios.delete(`${API}/api/operations/isolation-policies/${id}`, { headers });
      fetchPolicies();
      toast.success('Policy deleted');
    } catch (err) {
      toast.error('Failed to delete policy');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  const toggleEntityType = (entityType) => {
    const newTypes = partitionForm.entity_types.includes(entityType)
      ? partitionForm.entity_types.filter(t => t !== entityType)
      : [...partitionForm.entity_types, entityType];
    setPartitionForm({ ...partitionForm, entity_types: newTypes });
  };

  return (
    <div className="space-y-6" data-testid="operations-hub-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Shield className="h-6 w-6" />
            Operations Hub
          </h1>
          <p className="text-muted-foreground">Manage security, permissions, and data isolation</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full max-w-4xl">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks</TabsTrigger>
          <TabsTrigger value="partitions" data-testid="tab-partitions">Partitions</TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-roles">Roles</TabsTrigger>
          <TabsTrigger value="isolation" data-testid="tab-isolation">Isolation</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit Log</TabsTrigger>
        </TabsList>

        {/* Tasks Tab */}
        <TabsContent value="tasks">
          <OperationsTasksManager />
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Layers className="h-4 w-4" />
                  Data Partitions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboard?.partitions?.active || 0}</div>
                <p className="text-xs text-muted-foreground">of {dashboard?.partitions?.total || 0} total</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <UserCheck className="h-4 w-4" />
                  Roles Defined
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboard?.roles?.total || 0}</div>
                <p className="text-xs text-muted-foreground">with custom permissions</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  Isolation Policies
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboard?.isolation_policies?.active || 0}</div>
                <p className="text-xs text-muted-foreground">active policies</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Access Events (24h)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboard?.access_logs?.count_24h || 0}</div>
                <p className="text-xs text-muted-foreground">logged actions</p>
              </CardContent>
            </Card>
          </div>

          {/* Entity Stats */}
          {entityStats && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Entity Overview</CardTitle>
                <CardDescription>Records across all data types</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                  {Object.entries(entityStats).map(([entity, count]) => (
                    <div key={entity} className="text-center p-4 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold">{count}</p>
                      <p className="text-xs text-muted-foreground capitalize">{entity}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Access Logs */}
          {dashboard?.access_logs?.recent && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Access Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dashboard.access_logs.recent.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">No recent activity</p>
                  ) : (
                    dashboard.access_logs.recent.slice(0, 5).map((log) => (
                      <div key={log.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Badge variant="outline">{log.action}</Badge>
                          <div>
                            <p className="font-medium text-sm">{log.entity_type}</p>
                            <p className="text-xs text-muted-foreground">
                              {log.user?.first_name} {log.user?.last_name} • {log.ip_address}
                            </p>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground">{formatDate(log.timestamp)}</p>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Partitions Tab */}
        <TabsContent value="partitions" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Data Partitions</CardTitle>
                <CardDescription>Define data access boundaries for teams and departments</CardDescription>
              </div>
              <Button onClick={() => setPartitionModalOpen(true)} data-testid="create-partition-btn">
                <Plus className="h-4 w-4 mr-1" />
                Create Partition
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {partitions.length === 0 ? (
                  <div className="text-center py-8">
                    <Layers className="h-12 w-12 mx-auto text-slate-200 mb-2" />
                    <p className="text-muted-foreground">No partitions defined</p>
                    <p className="text-sm text-muted-foreground">Create partitions to segment data access</p>
                  </div>
                ) : (
                  partitions.map((partition) => (
                    <div key={partition.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${partition.is_active ? 'bg-green-100' : 'bg-slate-100'}`}>
                          <Layers className={`h-5 w-5 ${partition.is_active ? 'text-green-600' : 'text-slate-400'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{partition.name}</p>
                            <Badge variant="outline">{partition.partition_type}</Badge>
                            {partition.is_active ? (
                              <Badge className="bg-green-100 text-green-700 text-xs">Active</Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">Inactive</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{partition.description}</p>
                          <p className="text-xs text-muted-foreground">
                            Applies to: {partition.entity_types?.join(', ') || 'All entities'}
                          </p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => deletePartition(partition.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Roles Tab */}
        <TabsContent value="roles" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Role Permissions</CardTitle>
                <CardDescription>Configure permissions for each role</CardDescription>
              </div>
              <Button onClick={() => setRoleModalOpen(true)} data-testid="create-role-btn">
                <Plus className="h-4 w-4 mr-1" />
                Create Role
              </Button>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-4">
                  {roles.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">Loading roles...</p>
                  ) : (
                    roles.map((role) => (
                      <Card key={role.id} className="overflow-hidden">
                        <CardHeader className="pb-2 bg-slate-50">
                          <div className="flex items-center justify-between">
                            <div>
                              <CardTitle className="text-base capitalize flex items-center gap-2">
                                {role.role_name.replace(/_/g, ' ')}
                                {role.is_system_role && <Badge variant="secondary" className="text-xs">System</Badge>}
                              </CardTitle>
                              <CardDescription className="text-xs">{role.description}</CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-4">
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b">
                                  <th className="text-left py-2 px-2 font-medium">Entity</th>
                                  {PERMISSIONS.map(perm => (
                                    <th key={perm} className="text-center py-2 px-2 font-medium capitalize">{perm}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {ENTITY_TYPES.map(entity => (
                                  <tr key={entity} className="border-b last:border-0">
                                    <td className="py-2 px-2 capitalize">{entity}</td>
                                    {PERMISSIONS.map(perm => (
                                      <td key={perm} className="text-center py-2 px-2">
                                        <Checkbox
                                          checked={role.permissions?.[entity]?.[perm] || false}
                                          onCheckedChange={(checked) => updateRolePermission(role.id, entity, perm, checked)}
                                          disabled={role.is_system_role && role.role_name === 'admin'}
                                        />
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Isolation Tab */}
        <TabsContent value="isolation" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Data Isolation Policies</CardTitle>
                <CardDescription>Control how data is isolated between users</CardDescription>
              </div>
              <Button onClick={() => setPolicyModalOpen(true)} data-testid="create-policy-btn">
                <Plus className="h-4 w-4 mr-1" />
                Create Policy
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {policies.length === 0 ? (
                  <div className="text-center py-8">
                    <Lock className="h-12 w-12 mx-auto text-slate-200 mb-2" />
                    <p className="text-muted-foreground">No isolation policies defined</p>
                    <p className="text-sm text-muted-foreground">Create policies to control data visibility</p>
                  </div>
                ) : (
                  policies.map((policy) => (
                    <div key={policy.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${policy.is_active ? 'bg-blue-100' : 'bg-slate-100'}`}>
                          <Lock className={`h-5 w-5 ${policy.is_active ? 'text-blue-600' : 'text-slate-400'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{policy.name}</p>
                            <Badge variant="outline">{policy.isolation_type.replace(/_/g, ' ')}</Badge>
                            <Badge variant={policy.enforcement_level === 'strict' ? 'destructive' : 'secondary'} className="text-xs">
                              {policy.enforcement_level}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{policy.description}</p>
                          <p className="text-xs text-muted-foreground">
                            Entity: {policy.entity_type} • Field: {policy.isolation_field}
                          </p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => deletePolicy(policy.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Log Tab */}
        <TabsContent value="audit" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Access Audit Log</CardTitle>
                <CardDescription>Track all data access events</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchAccessLogs}>
                <RefreshCw className="h-4 w-4 mr-1" />
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-2">
                  {accessLogs.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No access logs</p>
                  ) : (
                    accessLogs.map((log) => (
                      <div key={log.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <Badge variant={
                            log.action === 'delete' ? 'destructive' :
                            log.action === 'export' ? 'secondary' : 'outline'
                          }>
                            {log.action}
                          </Badge>
                          <div>
                            <p className="font-medium text-sm">{log.entity_type}</p>
                            <p className="text-xs text-muted-foreground">
                              {log.user?.first_name} {log.user?.last_name} • {log.ip_address}
                            </p>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground">{formatDate(log.timestamp)}</p>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Partition Modal */}
      <Dialog open={partitionModalOpen} onOpenChange={setPartitionModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Data Partition</DialogTitle>
            <DialogDescription>Define a new data access partition</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                placeholder="Sales Team Partition"
                value={partitionForm.name}
                onChange={(e) => setPartitionForm({...partitionForm, name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Describe this partition..."
                value={partitionForm.description}
                onChange={(e) => setPartitionForm({...partitionForm, description: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Partition Type</Label>
              <Select value={partitionForm.partition_type} onValueChange={(v) => setPartitionForm({...partitionForm, partition_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="team">Team</SelectItem>
                  <SelectItem value="department">Department</SelectItem>
                  <SelectItem value="region">Region</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Applies to Entities</Label>
              <div className="flex flex-wrap gap-2">
                {ENTITY_TYPES.map(entity => (
                  <Badge
                    key={entity}
                    variant={partitionForm.entity_types.includes(entity) ? 'default' : 'outline'}
                    className="cursor-pointer capitalize"
                    onClick={() => toggleEntityType(entity)}
                  >
                    {entity}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={partitionForm.is_active}
                onCheckedChange={(checked) => setPartitionForm({...partitionForm, is_active: checked})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPartitionModalOpen(false)}>Cancel</Button>
            <Button onClick={createPartition} disabled={loading}>Create Partition</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Role Modal */}
      <Dialog open={roleModalOpen} onOpenChange={setRoleModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Custom Role</DialogTitle>
            <DialogDescription>Define a new role with custom permissions</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Role Name</Label>
              <Input
                placeholder="custom_role"
                value={roleForm.role_name}
                onChange={(e) => setRoleForm({...roleForm, role_name: e.target.value.toLowerCase().replace(/\s/g, '_')})}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Describe this role..."
                value={roleForm.description}
                onChange={(e) => setRoleForm({...roleForm, description: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleModalOpen(false)}>Cancel</Button>
            <Button onClick={createRole} disabled={loading}>Create Role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Policy Modal */}
      <Dialog open={policyModalOpen} onOpenChange={setPolicyModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Isolation Policy</DialogTitle>
            <DialogDescription>Define how data should be isolated</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Policy Name</Label>
              <Input
                placeholder="Owner-based Contact Isolation"
                value={policyForm.name}
                onChange={(e) => setPolicyForm({...policyForm, name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Describe this policy..."
                value={policyForm.description}
                onChange={(e) => setPolicyForm({...policyForm, description: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Isolation Type</Label>
                <Select value={policyForm.isolation_type} onValueChange={(v) => setPolicyForm({...policyForm, isolation_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="row_level">Row Level</SelectItem>
                    <SelectItem value="column_level">Column Level</SelectItem>
                    <SelectItem value="full">Full Isolation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Entity Type</Label>
                <Select value={policyForm.entity_type} onValueChange={(v) => setPolicyForm({...policyForm, entity_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ENTITY_TYPES.slice(0, 5).map(entity => (
                      <SelectItem key={entity} value={entity} className="capitalize">{entity}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Isolation Field</Label>
                <Input
                  placeholder="owner_id"
                  value={policyForm.isolation_field}
                  onChange={(e) => setPolicyForm({...policyForm, isolation_field: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Enforcement</Label>
                <Select value={policyForm.enforcement_level} onValueChange={(v) => setPolicyForm({...policyForm, enforcement_level: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="strict">Strict</SelectItem>
                    <SelectItem value="permissive">Permissive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPolicyModalOpen(false)}>Cancel</Button>
            <Button onClick={createPolicy} disabled={loading}>Create Policy</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OperationsHubPage;
